/**
 * UI05 + UI06: Left Panel Component
 * Icon strip (collapsed) + expanded panel with tab switching
 */

import './LeftPanel.css'
import { t } from '../i18n'

export type LayerTab = 'plants' | 'architecture' | 'ground' | 'metadata' | 'map' | 'favorites'

export interface TabContentRenderer {
  render(container: HTMLElement): void
  destroy(): void
  onActivate?(): void
  onDeactivate?(): void
}

interface LayerConfig {
  icon: string
  label: string
  color: string
  shortcut?: string
}

export const LAYER_CONFIG: Record<LayerTab, LayerConfig> = {
  plants: { icon: 'fa-seedling', label: 'Plants', color: '#92cc09' },
  architecture: { icon: 'fa-house', label: 'Architecture', color: '#FFBA1A' },
  ground: { icon: 'fa-layer-group', label: 'Ground', color: '#f76d33' },
  metadata: { icon: 'fa-tags', label: 'Metadata', color: '#8894d1' },
  map: { icon: 'fa-map-location-dot', label: 'Map', color: '#6ec1e4' },
  favorites: { icon: 'fa-star', label: 'Favorites', color: '#FFBA1A' }
}

const LAYER_ORDER: LayerTab[] = ['plants', 'architecture', 'ground', 'metadata', 'map']

export class LeftPanel {
  private container: HTMLElement
  private isExpanded: boolean = false
  private activeTab: LayerTab = 'plants'
  private tabRenderers: Map<LayerTab, TabContentRenderer> = new Map()
  private iconStrip: HTMLElement | null = null
  private panelContent: HTMLElement | null = null

  // Callbacks
  public onTabSelect: ((tab: LayerTab) => void) | null = null
  public onExpandedChange: ((expanded: boolean) => void) | null = null

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div')
    this.container.className = 'left-panel collapsed'
    parent.appendChild(this.container)

    this.render()
  }

  // ============ Public API ============

  expand(tab?: LayerTab): void {
    if (tab) {
      this.activeTab = tab
    }
    this.isExpanded = true
    this.container.classList.remove('collapsed')
    this.container.classList.add('expanded')
    this.updateIconStripActiveState()
    this.renderPanelContent()
    this.onExpandedChange?.(true)
  }

  collapse(): void {
    this.isExpanded = false
    this.container.classList.remove('expanded')
    this.container.classList.add('collapsed')
    this.deactivateCurrentTab()
    this.onExpandedChange?.(false)
  }

  toggle(): void {
    if (this.isExpanded) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  setActiveTab(tab: LayerTab): void {
    if (tab === this.activeTab && this.isExpanded) {
      // Clicking active tab collapses panel
      this.collapse()
      return
    }

    this.deactivateCurrentTab()
    this.activeTab = tab
    this.onTabSelect?.(tab)

    if (this.isExpanded) {
      this.updateIconStripActiveState()
      this.renderPanelContent()
    } else {
      this.expand(tab)
    }
  }

  getActiveTab(): LayerTab {
    return this.activeTab
  }

  isOpen(): boolean {
    return this.isExpanded
  }

  registerTabContent(tab: LayerTab, renderer: TabContentRenderer): void {
    this.tabRenderers.set(tab, renderer)
    // Re-render if this is the active tab
    if (this.isExpanded && this.activeTab === tab) {
      this.renderPanelContent()
    }
  }

  unregisterTabContent(tab: LayerTab): void {
    const renderer = this.tabRenderers.get(tab)
    if (renderer) {
      renderer.destroy()
      this.tabRenderers.delete(tab)
    }
  }

  destroy(): void {
    // Cleanup all tab renderers
    this.tabRenderers.forEach(renderer => renderer.destroy())
    this.tabRenderers.clear()
    this.container.remove()
  }

  // ============ Private Methods ============

  private render(): void {
    this.container.innerHTML = ''

    // Create icon strip
    this.iconStrip = this.createIconStrip()
    this.container.appendChild(this.iconStrip)

    // Create expanded content area
    this.panelContent = document.createElement('div')
    this.panelContent.className = 'panel-content'
    this.container.appendChild(this.panelContent)

    // Set initial lock state on all buttons
    this.updateIconStripActiveState()
  }

  private createIconStrip(): HTMLElement {
    const strip = document.createElement('div')
    strip.className = 'icon-strip'

    // Layer icons
    LAYER_ORDER.forEach(tab => {
      const btn = this.createIconButton(tab)
      strip.appendChild(btn)
    })

    // Separator
    const separator = document.createElement('div')
    separator.className = 'icon-strip-separator'
    strip.appendChild(separator)

    // Favorites
    const favBtn = this.createIconButton('favorites')
    strip.appendChild(favBtn)

    return strip
  }

  private createIconButton(tab: LayerTab): HTMLElement {
    const config = LAYER_CONFIG[tab]
    const btn = document.createElement('button')
    btn.className = 'icon-btn'
    btn.setAttribute('data-tab', tab)
    btn.setAttribute('data-tooltip', t(`layer.${tab}`, config.label))
    btn.innerHTML = `<i class="fa-solid ${config.icon}"></i>`

    btn.addEventListener('click', () => this.handleIconClick(tab))

    return btn
  }

  private handleIconClick(tab: LayerTab): void {
    this.setActiveTab(tab)
  }

  private updateIconStripActiveState(): void {
    if (!this.iconStrip) return

    const buttons = this.iconStrip.querySelectorAll('.icon-btn')
    buttons.forEach(btn => {
      const btnTab = btn.getAttribute('data-tab') as LayerTab
      const isActive = btnTab === this.activeTab && this.isExpanded
      btn.classList.toggle('active', isActive)
      btn.classList.toggle('locked', !isActive)

      // Apply layer color to all buttons (used for active bg and hover icon color)
      const config = LAYER_CONFIG[btnTab]
      ;(btn as HTMLElement).style.setProperty('--layer-color', config.color)
    })
  }

  private renderPanelContent(): void {
    if (!this.panelContent) return

    const config = LAYER_CONFIG[this.activeTab]

    // Create header
    this.panelContent.innerHTML = `
      <div class="panel-header">
        <i class="fa-solid ${config.icon}" style="color: ${config.color}"></i>
        <span class="panel-title">${t(`layer.${this.activeTab}`, config.label)}</span>
      </div>
      <div class="panel-body"></div>
    `

    // Render tab-specific content
    const body = this.panelContent.querySelector('.panel-body') as HTMLElement
    const renderer = this.tabRenderers.get(this.activeTab)

    if (renderer) {
      renderer.render(body)
      renderer.onActivate?.()
    } else {
      // Placeholder content
      body.innerHTML = `
        <div class="tab-placeholder">
          <p>${t(`layer.${this.activeTab}.placeholder`, `${config.label} content coming soon`)}</p>
        </div>
      `
    }
  }

  private deactivateCurrentTab(): void {
    const renderer = this.tabRenderers.get(this.activeTab)
    renderer?.onDeactivate?.()
  }
}
